'use client'
import CloseIcon from '@mui/icons-material/Close'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { useTranslations } from 'next-intl'
import { OptionsPopupProps } from '../../types'
import { AddToCartButton } from '../action-button'
import { CircularProgress } from '@mui/material'

// Styled Components
const PopupContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'white',
  zIndex: 20,
  borderRadius: theme.spacing(1),
  display: 'flex',
  flexDirection: 'column',
  boxShadow: theme.shadows[3],
}))

const PopupHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
}))

const PopupContent = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(2),
  overflowY: 'auto',
  position: 'relative',

  // Custom scrollbar styling
  '&::-webkit-scrollbar': {
    width: '6px',
    position: 'absolute',
    right: 0,
  },
  '&::-webkit-scrollbar-track': {
    background: theme.palette.grey[100],
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.grey[400],
    borderRadius: '3px',
    '&:hover': {
      background: theme.palette.grey[600],
    },
  },
  '&::-webkit-scrollbar-thumb:active': {
    background: theme.palette.grey[700],
  },

  // Firefox scrollbar styling
  scrollbarWidth: 'thin',
  scrollbarColor: `${theme.palette.grey[400]} ${theme.palette.grey[100]}`,
}))

const PopupFooter = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
}))
// const ProductImageContainer = styled(Box)({
//   width: 64,
//   height: 64,
//   flexShrink: 0,
// })
// const ProductInfo = styled(Box)({
//   flex: 1,
//   minWidth: 0,
// })

const ColorSwatch = styled(Box, {
  shouldForwardProp: (prop) => !['isSelected', 'isOutOfStock', 'swatchColor'].includes(prop as string),
})<{
  isSelected?: boolean
  isOutOfStock?: boolean
  swatchColor?: string
}>(({ theme, isSelected, isOutOfStock, swatchColor }) => ({
  width: 32,
  height: 32,
  borderRadius: '50%',
  border: '2px solid',
  borderColor: isSelected ? theme.palette.primary.main : theme.palette.grey[300],
  backgroundColor: swatchColor ?? '#0F172A',
  cursor: isOutOfStock ? 'not-allowed' : 'pointer',
  transition: 'all 0.2s',
  opacity: isOutOfStock ? 0.3 : 1,
  filter: isOutOfStock ? 'grayscale(50%)' : 'none',
  position: 'relative',

  '&:hover': {
    borderColor: !isOutOfStock ? theme.palette.primary.main : theme.palette.grey[300],
  },

  // Selected indicator
  ...(isSelected && {
    '&::after': {
      content: '""',
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: 8,
      height: 8,
      borderRadius: '50%',
      backgroundColor: 'white',
      boxShadow: '0 0 0 1px rgba(0,0,0,0.2)',
    },
  }),

  // Out of stock strike line
  ...(isOutOfStock && {
    '&::before': {
      content: '""',
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%) rotate(45deg)',
      width: '120%',
      height: 2,
      backgroundColor: theme.palette.error.main,
      zIndex: 1,
    },
  }),
}))

const StyledChip = styled(Chip, {
  shouldForwardProp: (prop) => !['isOutOfStock'].includes(prop as string),
})<{
  isOutOfStock?: boolean
}>(({ theme, isOutOfStock }) => ({
  cursor: isOutOfStock ? 'not-allowed' : 'pointer',
  opacity: isOutOfStock ? 0.5 : 1,
  textDecoration: isOutOfStock ? 'line-through' : 'none',

  '&.Mui-disabled': {
    color: theme.palette.text.disabled,
    borderColor: theme.palette.action.disabled,
  },
}))

const OptionLabel = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  fontWeight: 600,
  color: theme.palette.text.primary,
}))

// const PriceText = styled(Typography)(({ theme }) => ({
//   fontWeight: 700,
//   color: theme.palette.primary.main,
// }))

// const DiscountText = styled(Typography)(({ theme }) => ({
//   marginLeft: theme.spacing(1),
//   color: theme.palette.text.secondary,
//   textDecoration: 'line-through',
// }))

// Components
const ColorOption = ({ item, optionCode, isSelected, isOutOfStock, onSelect }: OptionItemProps) => (
  <ColorSwatch
    isSelected={isSelected}
    isOutOfStock={isOutOfStock}
    swatchColor={item?.swatch?.value}
    onClick={() => !isOutOfStock && onSelect(item?.uid, optionCode)}
  />
)

const TextOption = ({ item, optionCode, isSelected, isOutOfStock, onSelect }: OptionItemProps) => (
  <StyledChip
    label={item?.label}
    onClick={() => !isOutOfStock && onSelect(item?.uid, optionCode)}
    color={isSelected ? 'primary' : 'default'}
    variant={isSelected ? 'filled' : 'outlined'}
    disabled={isOutOfStock}
    isOutOfStock={isOutOfStock}
  />
)

const WishlistOptions = ({ dataWishlist }: { dataWishlist: IWishlistItem }) => (
  <>
    {dataWishlist.configurable_options?.map((el) => (
      <Box key={el?.configurable_product_option_uid} sx={{ mb: 1 }}>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {el?.option_label?.toLowerCase() === 'color' ? (
            <ColorSwatch swatchColor={el?.value_label ?? '#0F172A'} sx={{ borderColor: 'primary.main' }} />
          ) : (
            <Chip label={el?.value_label} color="primary" variant="filled" />
          )}
        </Stack>
      </Box>
    ))}
  </>
)
interface OptionItemProps {
  item: any
  optionCode: string
  isSelected: boolean
  isOutOfStock: boolean
  onSelect: (value: string, type: string) => void
}
export const OptionsPopup = ({
  show,
  onClose,
  data,
  productSelected,
  onChangeProduct,
  dataWishlist,
  customAddToCartBtn,
  loading,
  setLoading,
}: OptionsPopupProps) => {
  const t = useTranslations('Product')

  // Helper function to check if an option value is out of stock
  const isOptionOutOfStock = (optionCode: string, optionValue: string): boolean => {
    const variant = data?.variants?.find((variant) => {
      return variant?.attributes?.some((attr) => attr?.code === optionCode && attr?.uid === optionValue)
    })
    return variant?.product?.stock_status !== 'IN_STOCK'
  }

  const renderConfigurableOptions = () => {
    return data?.configurable_product_options_selection?.configurable_options?.map((el) => (
      <Box key={el?.uid} sx={{ mb: 2 }}>
        <OptionLabel variant="subtitle2">{el?.label}:</OptionLabel>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {el?.values?.map((item) => {
            const isSelected = item?.uid === productSelected?.options?.[el?.attribute_code]
            const isOutOfStock = isOptionOutOfStock(el?.attribute_code, item?.uid)

            const commonProps = {
              item,
              optionCode: el?.attribute_code,
              isSelected,
              isOutOfStock,
              onSelect: onChangeProduct,
            }

            return el?.attribute_code === 'color' ? (
              <ColorOption key={item?.uid} {...commonProps} />
            ) : (
              <TextOption key={item?.uid} {...commonProps} />
            )
          })}
        </Stack>
      </Box>
    ))
  }

  if (!show) return null

  return (
    <PopupContainer>
      <PopupHeader>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Select Options
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </PopupHeader>

      <PopupContent>
        {/* Product Info */}
        {/* <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <ProductImageContainer>
            <Image
              src={productSelected?.product?.product?.small_image?.url ?? data?.image?.url}
              width={64}
              height={64}
              alt=""
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                borderRadius: 4,
              }}
            />
          </ProductImageContainer>
          <ProductInfo>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                mb: 1,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {data?.name}
            </Typography>
            <PriceText variant="h6">
              {getVND(currentPrice?.final_price?.value || 0)}
              {Number(currentPrice?.discount?.amount_off || 0) > 0 && (
                <DiscountText variant="caption">
                  {getVND(currentPrice?.regular_price?.value || 0)}
                </DiscountText>
              )}
            </PriceText>
          </ProductInfo>
        </Box> */}

        {/* Options */}
        <Box>
          {dataWishlist ? <WishlistOptions dataWishlist={dataWishlist} /> : renderConfigurableOptions()}
        </Box>
      </PopupContent>

      <PopupFooter>
        <Stack spacing={1}>
          <AddToCartButton
            data={data}
            productSelected={[
              {
                ...productSelected,
                product: productSelected.product || data?.variants?.[0] || ({} as IVariantsProduct),
              },
            ]}
            dataWishlist={dataWishlist}
            setLocalLoading={setLoading}
            customAddToCartBtn={customAddToCartBtn}
            customRender={
              <Button variant="contained" fullWidth onClick={onClose} style={{ minHeight: 36.5 }}>
                {loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : t('add-to-cart')}
              </Button>
            }
          />
          <Button onClick={onClose} variant="outlined" fullWidth size="small">
            Cancel
          </Button>
        </Stack>
      </PopupFooter>
    </PopupContainer>
  )
}
